import React, { useEffect, useRef } from 'react';
import useTheme from '../hooks/useTheme';
// todo change the fallback to the svg logo
import svgLogo from '../assets/images/elia-group-logo-svg.svg';

// ThemedLogo: choose between colored (light) and white (dark) logos.
// Use PNGs from the public/ folder to avoid bundler errors if files are not present.
// Place your files in: public/images/Predico_Logotipo_Color.png and public/images/Predico_Logotipo_WHT.png
export default function ThemedLogo({ alt = 'Predico', className = '' }) {
  const { effectiveDark } = useTheme();

  // public URLs (won't break the build if the files are missing; they'll 404 at runtime)
  // Use empty string when PUBLIC_URL isn't set so paths become absolute (e.g. '/images/...')
  // Do NOT default to 'localhost:3000' which produces invalid relative paths in the browser.
  const publicUrl = process.env.PUBLIC_URL || '';

  // Build a small set of filename variants to be robust against spaces/casing/underscores
  const variantsFor = (base) => [
    `${publicUrl}/images/${base}.png`, // Predico_Logo_Color.png
    `${publicUrl}/images/${base.replace(/_/g, ' ')}.png`, // Predico Logotipo Color.png (space variant)
    `${publicUrl}/images/${base.toLowerCase()}.png`, // predico_logotipo_color.png (lowercase)
  ].map(encodeURI);

  const colorCandidates = variantsFor('Predico_Logotipo_Color');
  const whiteCandidates = variantsFor('Predico_Logotipo_WHT');
  const logo192 = encodeURI(`${publicUrl}/logo192.png`);

  // pick the primary candidate array depending on theme, then append logo192 as fallback
  const primaryCandidates = (effectiveDark ? whiteCandidates : colorCandidates).concat([logo192]);

  // Use refs so handlers always see the latest candidates and so we can reset the img when theme changes
  const candidatesRef = useRef(primaryCandidates);
  const imgRef = useRef(null);

  useEffect(() => {
    // update the ref whenever primaryCandidates changes
    candidatesRef.current = primaryCandidates;

    // if the theme changed, reset the image to the new primary candidate and reset attempt counter
    const img = imgRef.current;
    if (img) {
      img.dataset.attempt = '0';
      img.onerror = handleError; // attach handler (uses candidatesRef)
      img.src = primaryCandidates[0];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDark, publicUrl]);

  // onError handler that cycles through candidates stored on the img.dataset
  const handleError = (e) => {
    // eslint-disable-next-line no-param-reassign
    const img = e.currentTarget;
    img.onerror = null; // temporarily clear to avoid infinite loops
    const attempt = parseInt(img.dataset.attempt || '0', 10);
    const next = attempt + 1;
    const candidates = candidatesRef.current;
    if (next < candidates.length) {
      img.dataset.attempt = String(next);
      img.src = candidates[next];
      // reattach handler for subsequent failures
      img.onerror = handleError;
      return;
    }
    // final fallback to embedded svg
    img.src = svgLogo;
  };

  return (
    <img
      ref={imgRef}
      src={primaryCandidates[0]}
      data-attempt="0"
      alt={alt}
      className={className}
      onError={handleError}
    />
  );
}
